import React from 'react';
import { Box, Skeleton, Paper, Grid } from '@mui/material';

export interface AuditProgressBarSkeletonProps {
  compact?: boolean;
  showDetails?: boolean;
}

export const AuditProgressBarSkeleton: React.FC<AuditProgressBarSkeletonProps> = ({
  compact = false,
  showDetails = true,
}) => {
  if (compact) {
    return (
      <Box sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Skeleton
            variant="text"
            width={120}
            height={20}
            animation="wave"
            aria-label="Loading progress percentage"
          />
          <Box sx={{ flex: 1, mx: 2 }}>
            <Skeleton
              variant="rectangular"
              height={8}
              sx={{ borderRadius: 4 }}
              animation="wave"
              aria-label="Loading progress bar"
            />
          </Box>
          <Skeleton
            variant="rectangular"
            width={100}
            height={24}
            sx={{ borderRadius: 2 }}
            animation="wave"
            aria-label="Loading status chip"
          />
        </Box>
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Skeleton
          variant="text"
          width={150}
          height={28}
          animation="wave"
          aria-label="Loading audit progress title"
        />
        <Skeleton
          variant="rectangular"
          width={120}
          height={24}
          sx={{ borderRadius: 2 }}
          animation="wave"
        />
      </Box>

      {/* Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Skeleton variant="text" width={120} height={20} animation="wave" />
          <Skeleton variant="text" width={60} height={20} animation="wave" />
        </Box>
        <Skeleton variant="rectangular" height={12} sx={{ borderRadius: 6 }} animation="wave" />
      </Box>

      {showDetails && (
        <>
          {/* Statistics Grid */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[0, 1, 2, 3].map((index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Skeleton
                    variant="text"
                    width={60}
                    height={48}
                    sx={{ mx: 'auto' }}
                    animation={index < 2 ? 'wave' : false}
                  />
                  <Skeleton
                    variant="text"
                    width={100}
                    height={14}
                    sx={{ mx: 'auto' }}
                    animation={index < 2 ? 'wave' : false}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Additional Information */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Skeleton variant="text" width={200} height={20} animation="wave" />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Skeleton
                variant="rectangular"
                width={120}
                height={24}
                sx={{ borderRadius: 2 }}
                animation="wave"
              />
            </Box>
          </Box>

          {/* Inclusion Rate Section */}
          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Skeleton variant="text" width={180} height={20} animation="wave" />
              <Skeleton variant="circular" width={24} height={24} animation="wave" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Skeleton
                  variant="rectangular"
                  height={6}
                  sx={{ borderRadius: 3 }}
                  animation="wave"
                />
              </Box>
              <Skeleton variant="text" width={50} height={20} animation="wave" />
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};
